import { type FC, useRef, useState } from "react";
import { Button } from "antd";
import leftIcon from "../../../assets/images/left-arr.svg";
import rightGrayIcon from "../../../assets/images/right-arr-gray.svg";
import rightWhiteIcon from "../../../assets/images/right-arr-white.svg";
import { useFeatureFlags } from "../../../context/LDFlagsContext";
import { useTrialsRouter } from "../../../shared/custom-hooks/useTrialsRouter";
import { useTranslation } from "react-i18next";

const PLOT_DESIGN_STEP = 1;

export interface FormNavigationButtonsProps {
  current: number;
  cancelLabel: string;
  nextLabel: string;
  isStepValid: boolean;
  isNextDisabled: boolean;
  onCancelClick: () => void;
  onNextClick: () => void;
  // Persists Plot Design before opening the standalone Rates & Dosage route;
  // resolving false keeps the user on Plot Design.
  onSavePlotDesign?: () => Promise<boolean>;
  // Resolves only after affected cached reads are fresh; rejection stays on Design.
  onPrepareRatesAndDosage?: () => Promise<void>;
}

const FormNavigationButtons: FC<FormNavigationButtonsProps> = ({
  current,
  cancelLabel,
  nextLabel,
  isStepValid,
  isNextDisabled,
  onCancelClick,
  onNextClick,
  onSavePlotDesign,
  onPrepareRatesAndDosage,
}) => {
  const { t } = useTranslation();
  const { enableAutoProductAssignment } = useFeatureFlags();
  const { router, trialId } = useTrialsRouter();
  const saving = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const [transitionFailed, setTransitionFailed] = useState(false);
  const useNewRatesAndDosage =
    current === PLOT_DESIGN_STEP && enableAutoProductAssignment;

  // The standalone route has independent trial/assignment reads. Persist and
  // prepare them before navigating so they cannot reuse the previous design.
  const goToNewRatesAndDosage = async (): Promise<void> => {
    if (saving.current) return;
    saving.current = true;
    setIsSaving(true);
    setTransitionFailed(false);
    try {
      if (onSavePlotDesign && !(await onSavePlotDesign())) {
        setTransitionFailed(true);
        return;
      }
      if (onPrepareRatesAndDosage) await onPrepareRatesAndDosage();
      router.goToTrialsEditPage("new-rates-and-dosage", trialId);
    } catch {
      setTransitionFailed(true);
    } finally {
      saving.current = false;
      setIsSaving(false);
    }
  };

  return (
    <>
      {transitionFailed && (
        <div role="alert">
          {t("trials.ratesDosageReadinessError", {
            defaultValue:
              "Could not open Rates & Dosage. Retry Rates & Dosage. If the problem continues, refresh this page and try again.",
          })}
        </div>
      )}
      <div className="bottom-sec-btn">
        <Button
          className="cancel-btn"
          data-testid="cancel-btn"
          onClick={onCancelClick}
        >
          <img src={leftIcon} className="left-icon" alt="left-icon" />
          {cancelLabel}
        </Button>

        <Button
          className={`next-btn ${isStepValid ? "" : "active"}`}
          htmlType="button"
          data-testid="next-btn"
          disabled={isNextDisabled || isSaving}
          onClick={() => {
            if (useNewRatesAndDosage) {
              void goToNewRatesAndDosage();
            } else {
              onNextClick();
            }
          }}
        >
          {nextLabel}
          {nextLabel !== "Finish" && (
            <img
              src={isStepValid ? rightWhiteIcon : rightGrayIcon}
              alt="left-icon"
            />
          )}
        </Button>
      </div>
    </>
  );
};

export default FormNavigationButtons;
